import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import Axios from 'axios';
import { useImmerReducer } from 'use-immer';
import { Alert, Grid, Button, Modal, TextField, Typography, IconButton, Snackbar, FormHelperText, Autocomplete } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import AddRoundedIcon from '@mui/icons-material/AddCircleOutlined';
import { useTranslation } from 'react-i18next';
import StateContext from "../../Contexts/StateContext";
import DispatchContext from "../../Contexts/DispatchContext";
import AddScientist from './AddScientist';
import { formatDate } from '../../Utils/DateUtils';

const AddPublication = ({ open, onClose }) => {
    const { t, i18n } = useTranslation();
    const { config, environment } = require('../../index');
    const navigate = useNavigate();
    const GlobalState = useContext(StateContext);
    const dispatchGlobal = useContext(DispatchContext);
    const userId = GlobalState.userId;

    const initialState = {
        publicationTitle: '',
        publicationDate: dayjs(),
        publicationPlace: '',
        publicationType: '',
        publicationPublisher: '',
        publicationFullReference: '',
        sendRequest: 0,
        scientist: "",
        researchField: "",
        scientistFullNames: [],
        openSnack: false,
        disabledAddPublicationBtn: false,
        TitleError: { hasError: false, errorMessage: '' },
        DateError: { hasError: false, errorMessage: '' },
        PlaceError: { hasError: false, errorMessage: '' },
        PublisherError: { hasError: false, errorMessage: '' },
        FullReferenceError: { hasError: false, errorMessage: '' },
        ScientistError: { hasError: false, errorMessage: '' },
    };

    function ReducerFunction(draft, action) {
        switch (action.type) {
            case 'catchScientistChange':
                draft.scientist = action.scientistChosen || '';
                draft.ScientistError = { hasError: false, errorMessage: '' };
                break;
            case 'catchPublicationTitleChange':
                draft.publicationTitle = action.publicationTitleChosen || '';
                draft.TitleError = { hasError: false, errorMessage: '' };
                break;
            case 'catchPublicationDateChange':
                draft.publicationDate = action.publicationDateChosen || dayjs();
                draft.DateError = { hasError: false, errorMessage: '' };
                break;
            case 'catchPublicationTypeChange':
                draft.publicationType = action.publicationTypeChosen || '';
                break;
            case 'catchPublicationPlaceChange':
                draft.publicationPlace = action.publicationPlaceChosen || '';
                draft.PlaceError = { hasError: false, errorMessage: '' };
                break;
            case 'catchPublicationPublisherChange':
                draft.publicationPublisher = action.publicationPublisherChosen || '';
                draft.PublisherError = { hasError: false, errorMessage: '' };
                break;
            case 'catchPublicationFullReferenceChange':
                draft.publicationFullReference = action.publicationFullReferenceChosen || '';
                draft.FullReferenceError = { hasError: false, errorMessage: '' };
                break;
            case 'catchScientistFullNames':
                draft.scientistFullNames = action.scientistFullNames || [];
                break;
            case 'changeSendRequest':
                draft.sendRequest += 1;
                break;
            case 'reset':
                return { ...initialState };
            case 'openTheSnack':
                draft.openSnack = true;
                break;
            case 'allowAddPublicationBtn':
                draft.disabledAddPublicationBtn = false;
                break;
            case 'emptyTitle':
                draft.TitleError = { hasError: true, errorMessage: t('Validation.EmptyTitle', "A cím mező nem lehet üres!") };
                break;
            case 'emptyDate':
                draft.DateError = { hasError: true, errorMessage: t('Validation.EmptyDate', "A dátum mező nem lehet üres!") };
                break;
            case 'emptyFullReference':
                draft.FullReferenceError = { hasError: true, errorMessage: t('Validation.EmptyFullReference', "A teljes hivatkozás mező nem lehet üres!") };
                break;
            case 'emptyScientist':
                draft.ScientistError = { hasError: true, errorMessage: t('Validation.EmptyAuthor', "A szerző mező nem lehet üres!") };
                break;
            case 'updateResearchField':
                draft.researchField = action.researchField || '';
                break;
            default:
                break;
        }
    }

    const [state, dispatch] = useImmerReducer(ReducerFunction, initialState);
    const [isAddScientistModalOpen, setAddScientistModalOpen] = useState(false);

    const handleAddScientistClick = () => setAddScientistModalOpen(true);
    const handleReset = () => dispatch({ type: 'reset' });

    // Fetch scientist full names
    useEffect(() => {
        const endpoint = config[environment].scientistIdWithFullNameEndpoint;
        let isMounted = true;
        const fetchScientists = async () => {
            try {
                const res = await Axios.get(endpoint);
                if (!isMounted) return;
                const scientistFullNames = res.data.map(scientist => ({
                    ...scientist,
                    id: scientist.id,
                    full_name: scientist.full_name,
                    fields_of_science_id: scientist.fields_of_science_id,
                    fields_of_science_name: scientist.fields_of_science_name
                }));
                dispatch({ type: 'catchScientistFullNames', scientistFullNames });
            } catch (err) {
                console.error(err);
            }
        };
        fetchScientists();
        return () => { isMounted = false; };
    }, [config, environment, dispatch]);

    // Send publication
    useEffect(() => {
        if (!state.sendRequest) return;

        const data = new FormData();
        data.append('title', state.publicationTitle);
        data.append('publication_date', formatDate(state.publicationDate));
        data.append('publication_type', state.publicationType);
        data.append('publication_place', state.publicationPlace);
        data.append('publisher', state.publicationPublisher);
        data.append('full_reference', state.publicationFullReference);
        data.append('contributor', userId);
        data.append('field_of_science', state.scientistFullNames[0]?.fields_of_science_id || '');
        data.append('scientist', state.scientistFullNames[0]?.id || '');

        const addPublication = async () => {
            try {
                const endpoint = config[environment].createPublicationEndpoint;
                await Axios.post(endpoint, data);
                dispatch({ type: 'openTheSnack' });
            } catch (err) {
                dispatch({ type: 'allowAddPublicationBtn' });
                console.error(err);
            }
        };

        addPublication();
    }, [state.sendRequest, state.publicationTitle, state.publicationFullReference, state.scientistFullNames, state.publicationDate, state.publicationPlace, state.publicationPublisher, state.publicationType, dispatch, config, environment, userId]);

    // Navigate after snack
    useEffect(() => {
        if (state.openSnack) {
            const timer = setTimeout(() => navigate(`/${i18n.language}/academia`), 1500);
            return () => clearTimeout(timer);
        }
    }, [state.openSnack, navigate, i18n.language]);

    function SubmitButtonDisplay() {
        if (!GlobalState.userIsLogged) return <Button variant='outlined' onClick={() => navigate(`/${i18n.language}/login`)}>{t('Academy.LoginToAddObjects')}</Button>;
        if (!GlobalState.userProfile?.companyName || !GlobalState.userProfile?.phoneNumber) return <Button variant='outlined' onClick={() => navigate(`/${i18n.language}/profile`)}>{t('Academy.CompleteProfileToAdd')}</Button>;
        return <Button variant='contained' type='submit' disabled={state.disabledAddPublicationBtn}>{t('Common.Submit')}</Button>;
    }

    function FormSubmit(e) {
        e.preventDefault();
        if (!state.publicationTitle) dispatch({ type: 'emptyTitle' });
        else if (!state.publicationDate) dispatch({ type: 'emptyDate' });
        else if (!state.publicationFullReference) dispatch({ type: 'emptyFullReference' });
        else if (!state.scientist) dispatch({ type: 'emptyScientist' });
        else dispatch({ type: 'changeSendRequest' });
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Modal open={open} onClose={onClose}>
                <form onSubmit={FormSubmit}>
                    <Grid
                        item container direction="column" alignItems="stretch" justifyContent="center"
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '100%',
                            maxWidth: 400,
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            '@media (min-width: 768px)': { maxWidth: '66.66%' },
                            '@media (min-width: 992px)': { maxWidth: '50%' },
                        }}
                    >
                        <Grid container justifyContent="space-between">
                            <Typography variant='h4'>{t('Academy.AddPublication')}</Typography>
                            <IconButton onClick={onClose}><CloseIcon /></IconButton>
                        </Grid>

                        <Grid item sx={{ mt: 2 }}>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={6}>
                                    <Autocomplete
                                        options={state.scientistFullNames}
                                        getOptionLabel={(option) => `${option.full_name} (${option.fields_of_science_name})`}
                                        onChange={(e, value) => {
                                            dispatch({ type: 'catchScientistChange', scientistChosen: value?.full_name });
                                            dispatch({ type: 'updateResearchField', researchField: value?.fields_of_science });
                                        }}
                                        renderInput={(params) =>
                                            <TextField {...params} label={t('Academy.Author')} required fullWidth />}
                                        sx={{ mb: 2 }}
                                    />
                                </Grid>
                                <Grid item xs={1}>
                                    <IconButton color="primary" onClick={handleAddScientistClick}><AddRoundedIcon /></IconButton>
                                </Grid>
                                <Grid item xs={5}>
                                    <TextField variant="standard" label={t('Academy.PublicationType')}
                                               value={state.publicationType}
                                               onChange={(e) => dispatch({ type: 'catchPublicationTypeChange', publicationTypeChosen: e.target.value })}
                                               fullWidth />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item sx={{ mt: 2 }}>
                            <TextField variant="standard" label={t('Academy.Title')}
                                       value={state.publicationTitle}
                                       onChange={(e) => dispatch({ type: 'catchPublicationTitleChange', publicationTitleChosen: e.target.value })}
                                       fullWidth sx={{ mb: 2 }} />
                            <DatePicker
                                label={t('Academy.PublicationDate')}
                                value={state.publicationDate}
                                onChange={(newDate) => dispatch({ type: 'catchPublicationDateChange', publicationDateChosen: newDate })}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                        </Grid>

                        <Grid item sx={{ mt: 2 }}>
                            <TextField variant="standard" label={t('Academy.PublicationPlace')}
                                       value={state.publicationPlace}
                                       onChange={(e) => dispatch({ type: 'catchPublicationPlaceChange', publicationPlaceChosen: e.target.value })}
                                       fullWidth sx={{ mb: 2 }} />
                            <TextField variant="standard" label={t('Academy.Publisher')}
                                       value={state.publicationPublisher}
                                       onChange={(e) => dispatch({ type: 'catchPublicationPublisherChange', publicationPublisherChosen: e.target.value })}
                                       fullWidth sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item sx={{ mt: 2 }}>
                            <TextField variant="outlined" multiline rows={6} label={t('Academy.FullReference')}
                                       value={state.publicationFullReference}
                                       onChange={(e) => dispatch({ type: 'catchPublicationFullReferenceChange', publicationFullReferenceChosen: e.target.value })}
                                       fullWidth sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item sx={{ mt: 2 }}>
                            {SubmitButtonDisplay()}
                            <Button variant="contained" onClick={handleReset} sx={{ ml: 2 }}>{t('Common.Reset')}</Button>
                        </Grid>
                    </Grid>
                </form>
            </Modal>

            <Snackbar open={state.openSnack} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="success">{t('Academy.PublicationAddedSuccess', 'A publikációt sikeresen hozzáadtad!')}</Alert>
            </Snackbar>

            <AddScientist open={isAddScientistModalOpen} onClose={() => setAddScientistModalOpen(false)} />
        </LocalizationProvider>
    );
};

export default AddPublication;
